import { RefObject, useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href]:not([disabled]), button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';

const getVisibleFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(el => {
    if (el.getAttribute('aria-hidden') === 'true') {
      return false;
    }
    if (el.offsetWidth === 0 && el.offsetHeight === 0) {
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }
    return true;
  });

export interface UseDialogFocusTrapOptions {
  autoFocus?: boolean;
  restoreFocus?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
}

/**
 * Traps Tab navigation inside a dialog container and optionally restores focus on deactivate.
 */
export const useDialogFocusTrap = (
  isActive: boolean,
  containerRef: RefObject<HTMLElement | null>,
  options: UseDialogFocusTrapOptions = {}
): void => {
  const { autoFocus = true, restoreFocus = true, initialFocusRef } = options;
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const focusInitial = (): void => {
      const initialTarget = initialFocusRef?.current;
      if (initialTarget && container.contains(initialTarget)) {
        initialTarget.focus();
        return;
      }

      const focusable = getVisibleFocusableElements(container);
      if (focusable.length > 0) {
        focusable[0].focus();
        return;
      }

      container.tabIndex = -1;
      container.focus();
    };

    if (autoFocus) {
      requestAnimationFrame(focusInitial);
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') {
        return;
      }

      const focusable = getVisibleFocusableElements(container);
      if (!focusable.length) {
        return;
      }

      const active = document.activeElement as HTMLElement | null;

      if (!active || !container.contains(active)) {
        event.preventDefault();
        focusable[0].focus();
        return;
      }

      const currentIndex = focusable.indexOf(active);
      if (currentIndex === -1) {
        event.preventDefault();
        focusable[0].focus();
        return;
      }

      const isFirst = currentIndex === 0;
      const isLast = currentIndex === focusable.length - 1;

      if ((!event.shiftKey && isLast) || (event.shiftKey && isFirst)) {
        event.preventDefault();
        focusable[event.shiftKey ? focusable.length - 1 : 0].focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      if (restoreFocus && previousFocusRef.current) {
        try {
          previousFocusRef.current.focus();
        } catch {
          // Focus restore can fail if the trigger was removed from the DOM.
        }
      }
      previousFocusRef.current = null;
    };
  }, [isActive, containerRef, autoFocus, restoreFocus, initialFocusRef]);
};
